/* eslint-disable max-lines */
import * as clipboard from 'clipboard-polyfill'
import {Lang} from 'yougile-lang'
import {
  ContentTypeUtil,
  FilenameUtil,
  logger,
  ObjUtil,
  Option,
  Time,
  TimeInterval,
  Util
} from 'yougile-util'
import {
  addGTDStage,
  ChatMessage,
  Checklist,
  ChecklistItem,
  cleanChecklists,
  ColumnPermissions,
  createSubtaskFromTask,
  DataObjectId,
  DeadlineStickerEntry,
  DeadlineStickerValue,
  EntitledHubLike,
  getChecklists,
  getGTDLastChangeTime,
  getGTDStage,
  GTDStage,
  Hub,
  HubLike,
  movementTemplatesContainerId,
  noBoardContainerId,
  repeatTemplatesContainerId,
  SandboxTree,
  StorageDepartmentHierarchy,
  systemStickers,
  TaskColor,
  taskTemplatesContainerId,
  TimerStickerValue,
  User
} from 'yougile-model'
import {Items} from '@api/model/items'
import {Notifier} from '@api/model/notifier'
import {UIHandlers} from '@api/model/ui-handlers'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {DepartmentsClient} from '@client/departments-client'
import {PermissionsChecker} from '@client/permissions-checker'
import {PermissionsClientHelper} from '@client/permissions-client'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {SyncCommitHandler} from '@client/sync-commit-handler'
import {Backend} from '@common/backend'
import {DragData} from '@common/drag-data'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {MessageBox} from '@components/message-box'
import {TaskItem} from '@components/my-tasks/task-item'
import {Gantt} from '@extensions/gantt'
import {GanttDraft} from '@extensions/gantt-draft'
import {StopwatchCtl} from '@extensions/sticker/controllers/stopwatch-ctl'
import {SubtaskUtil} from '@extensions/subtask-util'
import {updateWhenTaskDone} from './board-settings/update-when-task-done'
import {ChatSubscribers} from './chat-subscribers'
import {Column} from './column'
import {globals} from './globals-base'
import {idTasksApi} from './id-tasks/id-tasks-store'
import {PageService} from './page-service'
import {SeenBy} from './seen-by'
import {BoardFilters} from './sticker/board-filters'
import {Sticker, stickerCtl, systemStickerCtl} from './sticker/sticker'
import {SystemEntities} from './system-entities'
import {taskPhantomObserver} from './task-phantom-observer'
import {UserAccess} from './user-access'

export const excludeTaskTemplateStickers = [
  'Timetracking',
  'Repeat',
  'Stopwatch',
  'User'
]
export const includeTaskTemplateStickers = ['Timer', 'Deadline']

export class Task {
  static allTasks(
    companyId: DataObjectId,
    hierarchy: StorageDepartmentHierarchy | 'ignore-departments'
  ): Hub[] {
    const res: Hub[] = []
    Task.forEachTask(Sync.get({companyId}), hierarchy, t => res.push(t))
    return res
  }

  static allTasksWithDeleted(companyId: DataObjectId): Hub[] {
    const box = Sync.get({companyId})
    const res: Hub[] = []

    box.forAllObjects(obj => {
      if (obj instanceof Hub && box.getType(obj) === 'task') {
        res.push(obj)
      }
    })
    return res
  }

  static forEachTask(
    box: SandboxTree,
    hierarchy: StorageDepartmentHierarchy | 'ignore-departments',
    fn: (task: Hub) => void
  ): void {
    const calculatedHierarchy =
      hierarchy === 'ignore-departments'
        ? DepartmentsClient.getHierarchy(box)
        : hierarchy
    const checkCache = PermissionsChecker.makeCheckCache()
    box.walkForward(box.getHub(box.companyId), (obj, level) => {
      if (obj.id === noBoardContainerId) {
        return true
      }

      if (obj.deleted || obj.title.startsWith('.')) {
        if (
          hierarchy !== 'ignore-departments' &&
          obj.deleted &&
          obj.title === obj.id
        ) {
          // case of forbidden project/board/column
          return true
        } else {
          return false
        }
      }
      if (level === 4 || obj.location === noBoardContainerId) {
        if (box.isDeleted(obj.id)) {
          return false
        }
        if (
          PermissionsChecker.checkShow(hierarchy, obj, {
            hasNoChildrenHint: true,
            calculatedHierarchy,
            checkCache
          })
        ) {
          fn(obj)
        }
        return false
      }
      return level < 4
    })
  }

  static find(task: {companyId: DataObjectId; id: DataObjectId}): Hub {
    if (task instanceof Hub) {
      return task
    }
    const box = Sync.get(task)
    return box && (box.get(task.id) as Hub)
  }

  static editTitle(task: EntitledHubLike, title: string): void {
    if (task.title !== title) {
      const hub = Task.find(task)
      const canEdit = Items.rename(Items.fromHub(hub), title)

      if (canEdit) {
        Sync.get(task).editTitle(hub, title)
      }
    }
  }

  static pinDeadline(
    task: Hub,
    deadline: DeadlineStickerEntry,
    companyId?: DataObjectId
  ) {
    const box = Sync.get({companyId: companyId ?? globals.getCompanyId()})
    const dl = stickerCtl.Deadline.getSticker(box)
    const dlVal: DeadlineStickerValue = {current: deadline, history: []}
    Sticker.pin(dl, task.location, task, dlVal)
  }

  static pinFilters(task: Hub, type: 'create-task' | 'create-via-calendar') {
    const board = PageService.getCurrentBoard()
    BoardFilters.pinFiltersToTask(board, task, false, type)
    // task.commit()
    SeenBy.markSeen(task, globals.currentUserId)
  }

  // eslint-disable-next-line max-lines-per-function
  static createTask(
    title: string,
    opts: {
      location?: DataObjectId
      allowApi?: boolean
      noAutoSubscribe?: boolean
      deadline?: DeadlineStickerEntry
      assigned?: DataObjectId[]
      assignedBy?: DataObjectId
      resolveNameConflicts?: boolean
      isPrivate?: boolean
      companyId?: DataObjectId
      toEnd?: boolean
      markSeen?: boolean
      isCreatedByDefault?: boolean
    }
  ): Hub | undefined {
    const {
      location,
      deadline,
      allowApi,
      assigned,
      assignedBy,
      resolveNameConflicts,
      noAutoSubscribe,
      isPrivate,
      companyId,
      toEnd = false,
      markSeen = false
    } = opts

    title = title.trim()
    const box = Sync.get({companyId: companyId ?? globals.getCompanyId()})
    const colId = location ? location : noBoardContainerId
    const col = colId ? (box.get(colId) as Hub) : undefined

    if (col && Column.isMirror(col)) {
      throw new Error('Not allowed')
    }

    const newTask = new Hub(box.companyId, title)
    if (isPrivate) {
      newTask.data.private = globals.currentUserId
    }
    newTask.data.by = globals.currentUserId

    if (opts.isCreatedByDefault) {
      newTask.data.isCreatedByDefault = opts.isCreatedByDefault
    }

    // if user is only allowed to see tasks, assigned to her then
    // automaticly self-assign on newly created task
    if (col && !SharedBoardsClient.getId()) {
      const isAdmin = PermissionsChecker.isAdmin()
      const proj = box.getProjectFor(col)
      const role = UserAccess.getRoleInProject(globals.currentUserId, proj)
      if (!isAdmin && col && !role) {
        const po = PermissionsClientHelper.getPermissions(
          box.companyId,
          col.id,
          globals.currentUserId
        )
        const perm = po?.permissions as ColumnPermissions
        if (perm?.myTasks?.show && !perm.allTasks?.show) {
          const userTag = stickerCtl.User.getSticker(box)
          if (userTag) {
            newTask.stickers[userTag.id] = globals.currentUserId
          }
        }
      }
    }

    if (!!col && allowApi && globals.getCompanyId() === col.companyId) {
      if (!Items.beforeAdd(Items.fromHub(col), title)) {
        return undefined
      }
    }

    box.add(newTask, colId, resolveNameConflicts, false)
    if (col && colId !== noBoardContainerId && !SharedBoardsClient.getId()) {
      Column.getTasksOrder(col).addEntity(newTask, toEnd)
    }

    if (deadline) {
      Task.pinDeadline(newTask, deadline, companyId)
    }

    setTimeout(() => {
      if (SharedBoardsClient.getId()) {
        return
      }
      if (assignedBy) {
        Task.addAssignedBy(newTask, assignedBy)
      }

      if (assigned && assigned.length > 0) {
        Sticker.setUsers(newTask, assigned, newTask.location)
        if (!noAutoSubscribe) {
          for (const assignee of assigned) {
            ChatSubscribers.safeSubscribe(assignee, newTask)
          }
        }
      }
      if (!noAutoSubscribe) {
        ChatSubscribers.safeSubscribe(globals.currentUserId, newTask)
      }
      if (col && allowApi && globals.getCompanyId() === col.companyId) {
        Items.add(Items.fromHub(newTask))
      }
    }, 100)

    if (markSeen) {
      SeenBy.markSeen(newTask, globals.currentUserId)
    }

    return newTask
  }

  static getAssignedBy(task: HubLike): DataObjectId[] {
    const hub = Task.find(task)
    if (!hub) {
      return []
    }
    const res: DataObjectId[] = []
    if (hub.data.assignedBy) {
      Object.keys(hub.data.assignedBy).forEach((uid: DataObjectId) =>
        res.push(uid)
      )
    } else {
      const creator = Task.creator(hub)
      const assigned = Sticker.getUsers(hub)
      if (creator && assigned.filter(uid => uid !== creator.id).length > 0) {
        res.push(creator.id)
      }
    }
    return res
  }

  static addAssignedBy(task: HubLike, userId: DataObjectId): void {
    const hub = Task.find(task)
    if (!hub || !userId) {
      return
    }
    if (PermissionsChecker.checkAssignUser(hub, userId)) {
      hub.data.assignedBy = hub.data.assignedBy || {}
      if (!hub.data.assignedBy[userId]) {
        hub.data.assignedBy[userId] = Time.now()
        hub.commit()
      }
    } else {
      logger.error('Can‘t set this assigned by', userId)
    }
  }

  static isArchived(
    task: HubLike,
    companyId: DataObjectId = task.companyId
  ): boolean {
    const box = Sync.get({companyId})
    if (!box) {
      return false
    }
    const hub = box.get(task.id) as Hub
    return !!(hub?.complete && hub.complete > 0)
  }

  static forceChangeCompleteness(task: HubLike, isCompleted: boolean): void {
    const box = Sync.get(task)
    const system = SystemEntities.get(task.companyId)
    const hub = Task.find(task)
    const current = Task.isArchived(task)
    if (isCompleted && !current) {
      box.connect(system.taskCompletedTagId, task.id)
      hub.complete = Time.now()
      hub.commit()
    } else if (!isCompleted && current) {
      box.disconnect(system.taskCompletedTagId, task.id)
      hub.complete = undefined
      hub.commit()
    }
  }

  static async changeArchived(
    task: HubLike,
    isArchived: boolean
  ): Promise<boolean> {
    const box = Sync.get(task)
    const system = SystemEntities.get(task.companyId)
    const hub = Task.find(task)
    if (isArchived) {
      box.connect(system.taskCompletedTagId, task.id)
      hub.complete = Time.now()
      hub.commit()
    } else {
      box.disconnect(system.taskCompletedTagId, task.id)
      hub.complete = undefined
      hub.commit()
    }
    Items.changeArchived(Items.fromHub(hub))
    GanttDraft.planEntitySubtree(hub.id, box)
    return true
  }

  static setChecklists(task: Hub, checklists: Checklist[]): void {
    if (!checklists) {
      delete task.data.checklists
      this.commitWithCheckPhantom(task)
    } else {
      task.data.checklists = cleanChecklists(checklists)
      this.commitWithCheckPhantom(task)
    }
  }

  static updateChecklist(task: Hub, checklist: Checklist): void {
    const lists = getChecklists(task) || []
    const ix = lists.findIndex((cl: Checklist) => cl.id === checklist.id)
    if (ix !== -1) {
      lists[ix] = checklist
      Task.setChecklists(task, lists)
    }
  }

  static deleteChecklist(task: Hub, checklistId: DataObjectId): void {
    const lists = getChecklists(task) || []
    const ix = lists.findIndex(cl => cl.id === checklistId)
    if (ix !== -1) {
      lists.splice(ix, 1)
      Task.setChecklists(task, lists)
    }
  }

  static addChecklist(task: Hub, title: string, toEnd?: boolean): DataObjectId {
    const lists = getChecklists(task) || []
    const newList: Checklist = {
      id: Util.UUID() as DataObjectId,
      title,
      items: []
    }
    if (toEnd) {
      lists.push(newList)
    } else {
      lists.unshift(newList)
    }
    Task.setChecklists(task, lists)
    return newList.id
  }

  static renameChecklist(
    task: Hub,
    checklistId: DataObjectId,
    title: string
  ): void {
    const lists = getChecklists(task) || []
    const ix = lists.findIndex((cl: Checklist) => cl.id === checklistId)
    if (ix !== -1) {
      lists[ix].title = title
      Task.setChecklists(task, lists)
    }
  }

  static addSubtask(
    task: Hub,
    checklistId: DataObjectId,
    title: string,
    isCompleted = false
  ): DataObjectId | undefined {
    const lists = getChecklists(task) || []
    const ix = lists.findIndex((cl: Checklist) => cl.id === checklistId)
    if (ix !== -1) {
      const newSub = {
        id: Util.UUID() as DataObjectId,
        title,
        isCompleted
      }
      lists[ix].items.push(newSub)
      Task.setChecklists(task, lists)
      return newSub.id
    }
    return undefined
  }

  static toggleSubtaskCompleted(
    task: Hub,
    checklistId: DataObjectId,
    subtaskId: DataObjectId
  ): void {
    const lists = getChecklists(task) || []
    Task.findSubtask(lists, checklistId, subtaskId, (s: ChecklistItem) => {
      s.isCompleted = !s.isCompleted
      Task.setChecklists(task, lists)
    })
  }

  static deleteSubtask(
    task: Hub,
    checklistId: DataObjectId,
    subtaskId: DataObjectId
  ): void {
    const lists = getChecklists(task) || []
    Task.findSubtask(
      lists,
      checklistId,
      subtaskId,
      (_: ChecklistItem, ix: number, subIx: number) => {
        const {items} = lists[ix]
        items.splice(subIx, 1)
        Task.setChecklists(task, lists)
      }
    )
  }

  static renameSubtask(
    task: Hub,
    checklistId: DataObjectId,
    subtaskId: DataObjectId,
    title: string
  ): void {
    const lists = getChecklists(task) || []
    Task.findSubtask(lists, checklistId, subtaskId, (s: ChecklistItem) => {
      s.title = title
      Task.setChecklists(task, lists)
    })
  }

  static findSubtask(
    all: Checklist[],
    listId: DataObjectId,
    subtaskId: DataObjectId,
    cb: (s: ChecklistItem, ix: number, subIx: number) => void
  ): void {
    const ix = all.findIndex((cl: Checklist) => cl.id === listId)
    if (ix !== -1) {
      const {items} = all[ix]
      const subIx = items.findIndex((s: ChecklistItem) => s.id === subtaskId)
      if (subIx !== -1) {
        cb(items[subIx], ix, subIx)
      }
    }
  }

  /**
   * Currently only subtasks without board are replaced (by design)
   */
  private static replaceSubtasksWithDuplicated(
    box: SandboxTree,
    dup: Hub,
    duplicatedSubtaskIds?: Set<DataObjectId>,
    oldNewMap: Map<DataObjectId, DataObjectId> = new Map()
  ): void {
    duplicatedSubtaskIds ??= new Set()
    const list = dup.data.subtaskList
    const subtasks = list?.subtasks
    const order = list?.order
    if (!subtasks || !order) {
      return
    }

    const subtaskIds = duplicatedSubtaskIds
    SyncCommitHandler.doInBatch(() => {
      ObjUtil.forEach(subtasks, (id, sub) => {
        delete subtasks[id]
        const subObj = box.getHub(sub.id)
        if (subtaskIds.has(sub.id) || !subObj) {
          return
        }
        if (subObj.deleted) {
          return
        }
        subtaskIds.add(sub.id)
        if (subObj.location !== noBoardContainerId) {
          subtasks[id] = sub
          return
        }
        const subDup = this.duplicate(subObj, undefined, subtaskIds, oldNewMap)
        if (!subDup) {
          return
        }
        const newSubtask = createSubtaskFromTask(subDup)
        if (!newSubtask) {
          return
        }
        subtasks[subDup.id] = newSubtask
        const ordIx = order.indexOf(sub.id)
        if (ordIx > -1) {
          order[ordIx] = subDup.id
        } else {
          order.push(subDup.id)
        }
      })

      list.order = list.order.filter(id => !!subtasks[id])
    })
  }

  /**
   * Creates a copy of task but with new chat and places the new task
   * in the same column after the original task.
   *
   * Copied properties: name, description, stickers, assigned user,
   * subtasks, connected tasks, chat subscribers.
   */
  static duplicate(
    task: Hub,
    toColumn?: Hub,
    duplicatedSubtaskIds?: Set<DataObjectId>,
    oldNewMap?: Map<DataObjectId, DataObjectId>
  ): Hub | undefined {
    const localOldNewMap = new Map<DataObjectId, DataObjectId>()

    const box = Sync.get(task)
    const col = toColumn || (box.get(task.location) as Hub)
    const title = box.notConflictingTitle(task.title, 'Hub', col.id)

    const dup = Task.createTask(title, {
      location: col.id,
      noAutoSubscribe: true
    })
    if (!dup) {
      return undefined
    }
    localOldNewMap.set(task.id, dup.id)
    dup.data = Util.clone(task.data)
    delete dup.data.mirrors
    dup.data.by = globals.currentUserId
    this.replaceSubtasksWithDuplicated(
      box,
      dup,
      duplicatedSubtaskIds,
      localOldNewMap
    )

    dup.complete = task.complete ? Time.now() : undefined
    if (task.properties) {
      dup.properties = Util.clone(task.properties)
    }
    dup.description = task.description
    if (task.stickers) {
      dup.stickers = Util.clone(task.stickers)
      if (dup.stickers[systemStickers.Repeat]) {
        delete dup.stickers[systemStickers.Repeat]
      }
    }
    dup.commit()
    Items.add(Items.fromHub(dup))

    const subscribers = ChatSubscribers.getSubscribedUserIds(task)
    subscribers.forEach(userId => {
      if (!ChatSubscribers.isSubscribed(userId, dup)) {
        ChatSubscribers.subscribe(userId, dup)
      }
    })

    if (!toColumn && col.id !== noBoardContainerId) {
      const ord = Column.getTasksOrder(col)
      const ix = ord.order(task)
      ord.handleDrop(ix, dup)
    }
    SeenBy.markSeen(dup, globals.currentUserId)

    if (oldNewMap) {
      for (const [oldId, newId] of localOldNewMap) {
        oldNewMap.set(oldId, newId)
      }
    } else {
      SyncCommitHandler.doInBatch(() => {
        for (const newId of localOldNewMap.values()) {
          Gantt.relinkTaskDependencies(newId, localOldNewMap)
        }
      })
    }

    return dup
  }

  static getLocations(taskId: DataObjectId) {
    const task = Task.getById(taskId)
    if (!task) {
      return undefined
    }
    if (task.deleted) {
      return undefined
    }

    if (task.location === noBoardContainerId) {
      return {
        parentTasks: SubtaskUtil.getParentTaskIds(taskId)
          .map(id => Task.getById(id))
          .filter(Util.isDefined)
      }
    }
    return {
      column: this.parentColumn(task),
      board: this.parentBoard(task),
      project: this.parentProject(task)
    }
  }

  static parentColumn(task: HubLike): Hub | undefined {
    if (!task) {
      return undefined
    }
    const box = Sync.get(task)
    if (!box) {
      return undefined
    }
    const item = box.getHub(task.id)
    if (!item) {
      return undefined
    }
    if (this.isRepeatTemplate(item)) {
      return box.getHub(item.data.repeatTemplate?.columnId)
    }
    if (this.isTaskTemplate(item)) {
      return undefined
    }
    return box.getHub(item.location)
  }

  static parentBoard(task: HubLike): Hub | undefined {
    const col = Task.parentColumn(task)
    if (col) {
      return Sync.parentHub(col)
    }
    const box = Sync.get(task)
    const item = box.getHub(task.id)
    return this.isTaskTemplate(item)
      ? box.getHub(item?.data.taskTemplate?.boardId)
      : undefined
  }

  static parentProject(task: HubLike): Hub | undefined {
    const board = Task.parentBoard(task)
    return board && Sync.parentHub(board)
  }

  static creator(task: Hub | undefined): User | undefined {
    if (!task) {
      return undefined
    }
    const box = Sync.get(task)
    if (task.data.by) {
      return box.get(task.data.by) as User
    }
    const systemEntities = SystemEntities.get(box.companyId)
    if (!systemEntities) {
      return undefined
    }
    const createdByTag = systemEntities.createdByTagId

    if (task && box.isConnected(createdByTag, task.id)) {
      const userId = box.getModifiers(createdByTag, task.id)[0]

      if (userId) {
        return box.get(userId as DataObjectId) as User
      }
    }

    return undefined
  }

  static clearGTDStage(task: HubLike): void {
    const hub = Task.find(task)
    if (!hub) {
      return
    }
    if (hub.data.gtd) {
      delete hub.data.gtd
      hub.commit()
    }
  }

  private static setGTDStage(
    task: HubLike,
    stage: GTDStage,
    reason = 'unknown'
  ): void {
    const hub = Task.find(task)
    if (!hub) {
      return
    }

    if (!Items.complete(Items.fromHub(hub))) {
      return
    }

    const gtd = hub.data.gtd
    hub.data.gtd = addGTDStage(gtd, stage, reason)

    if (stage === 'done') {
      updateWhenTaskDone(hub)
    }
    hub.commit()
    const isInitialisation = !gtd && stage === 'new'
    if (!isInitialisation) {
      UIHandlers.triggerTaskDone(hub)
      logger.log('UIHandlers.triggerTaskDone is called')
    } else {
      logger.log('UIHandlers.triggerTaskDone is skipped due to initialisation')
    }
  }

  static toggleStopwatch(hub: Hub, stage: GTDStage): void {
    const board = Task.getBoardFromTask(hub)
    if (!board) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const sticker = stickerCtl.Stopwatch.getSticker(box)
    const isStickerActiveInBoard = Sticker.isActiveInBoard(board, sticker)
    if (!isStickerActiveInBoard) {
      return
    }
    const stickerSettings = board?.data.stickersSettings?.[sticker.id]
    if (!stickerSettings?.shouldStopWhenCompleted) {
      return
    }
    const stopwatch = hub.stickers[sticker.id]
    if (!stopwatch) {
      return
    }

    if (!PermissionsChecker.checkEditTaskStickers(hub, sticker, stopwatch)) {
      Notifier.warn(Lang.msg.noPermissionToEditStopwatchOnComplete)
      return
    }

    const gtd = addGTDStage(hub.data.gtd, stage, 'stopwatch-toggled')
    const history = stopwatch.history || []
    if (stopwatch?.current) {
      history.push(stopwatch.current)
    }
    const oldTi = TimeInterval.fromString(stopwatch.current.time)
    const stopwatchCtl = new StopwatchCtl()
    const newTi = oldTi.add(
      stopwatchCtl.intervalFrom(stopwatch.current.timestamp)
    )
    const newVal: TimerStickerValue = {
      current: {
        time: stopwatch.current.running ? newTi.toString() : oldTi.toString(),
        by: globals.currentUserId,
        timestamp: Time.now(),
        running: gtd.state?.stage !== 'done'
      },
      history
    }
    Sticker.pin(sticker, hub.id, hub, newVal)
  }

  static insertInGTD(task: HubLike, stage: GTDStage, reason: string): void {
    const hub = Task.find(task)
    if (!hub) {
      return
    }
    Task.toggleStopwatch(hub, stage)
    const oldStage = Task.getGTDStage(task)
    if (oldStage !== stage) {
      Task.setGTDStage(task, stage, reason)
    }
  }

  static getGTDStage(task: HubLike): GTDStage | undefined {
    const hub = Task.find(task)
    const {gtd} = Option.from(hub)
      .map(h => h.data)
      .getOrElse({})
    return getGTDStage(gtd)
  }

  static getGTDLastChangeTime(task: HubLike): number {
    const hub = Task.find(task)
    const {gtd} = Option.from(hub)
      .map(h => h.data)
      .getOrElse({})
    return getGTDLastChangeTime(gtd, hub?.timestamp)
  }

  static getCompletionTime(task: Hub | undefined): number | undefined {
    if (!task) {
      return undefined
    }
    const stage = Task.getGTDStage(task)
    return stage === 'done'
      ? Math.max(Task.getGTDLastChangeTime(task) || 0, task.timestamp)
      : undefined
  }

  static changeCompleted(
    task: HubLike,
    isCompleted: boolean,
    fromWhere?: 'my-tasks' | 'board' | 'right-panel' | 'timeline'
  ): void {
    const stage = Task.getGTDStage(task)
    const hub = Task.find(task)
    if (stage === 'done') {
      if (!isCompleted) {
        // fromWhere === 'my-tasks' &&
        //   void Tracking.send(TrackingEvent.myTasksUncomplete)
        // fromWhere === 'board' &&
        //   void Tracking.send(TrackingEvent.taskUncompleted)
        // fromWhere === 'right-panel' &&
        //   void Tracking.send(TrackingEvent.rightPanelTaskUncompleted)

        fromWhere === 'timeline' &&
          void Tracking.send(TrackingEvent.ganttIncompleteTaskViaGanttChart)

        Task.insertInGTD(
          task,
          'new',
          `Task.changeCompleted -> !isCompleted (${fromWhere})`
        )
      }
    } else {
      if (isCompleted) {
        if (fromWhere === 'my-tasks') {
          if (hub?.data?.private && !hub.data?.isCreatedByDefault) {
            void Tracking.send(
              TrackingEvent.myTasksCompletePrivateTaskViaMyTaskPage
            )
          } else {
            void Tracking.send(
              TrackingEvent.myTasksCompleteTaskViaMyTaskSection
            )
          }
        }
        fromWhere === 'board' &&
          void Tracking.send(TrackingEvent.taskCompleteTaskViaBoard)

        // complete via right panel menu
        fromWhere === 'right-panel' &&
          void Tracking.send(TrackingEvent.taskCompleteTaskViaRightPanel)

        fromWhere === 'timeline' &&
          void Tracking.send(TrackingEvent.ganttCompleteTaskViaGanttChart)

        Task.insertInGTD(
          task,
          'done',
          `Task.changeCompleted -> isCompleted (${fromWhere})`
        )
      }
    }
  }

  static bunchDelete(box: SandboxTree, taskIds: DataObjectId[]): void {
    SyncCommitHandler.doInBatch(() => {
      for (const id of taskIds) {
        const t = box.getHub(id)
        if (t) {
          box.remove(t)
          GanttDraft.planEntitySubtree(t.id, box)
        }
      }
    })
  }

  static getLinkOldFormat(task: Hub | TaskItem): string {
    const host = location.host
      ? location.protocol + '//' + location.host
      : Backend.toMainUrl('')

    const shortCompanyId = Util.shortId(task.companyId)

    const shortTaskId = Util.shortId(task.id)
    return `${host}/team/${shortCompanyId}/#chat:${shortTaskId} `
  }

  static getLink(task: Hub | TaskItem): string {
    const idTask = idTasksApi.getIdTask(task?.id)

    const host = location.host
      ? location.protocol + '//' + location.host
      : Backend.toMainUrl('')

    const shortCompanyId = Util.shortId(task.companyId)

    if (idTask) {
      return `${host}/team/${shortCompanyId}/#${idTask}`
    } else {
      const shortTaskId = Util.shortId(task.id)
      return `${host}/team/${shortCompanyId}/#chat:${shortTaskId}`
    }
  }

  static copyLink(task: Hub | TaskItem): void {
    const url = Task.getLink(task)

    clipboard
      .writeText(url)
      .then(() =>
        MessageBox.show(Lang.context.common.taskLinkCopied, 'success', {
          timer: 2000
        })
      )
      .catch(e => logger.log('err', e))
  }

  static getById(id: DataObjectId): Hub | undefined {
    return Sync.getCurrentSandbox().getHub(id)
  }

  static getTitle(taskId: DataObjectId): string {
    const task = Task.getById(taskId)
    if (!task) {
      return ''
    }

    const directTitle = task.title
    if (!Util.isUUID(directTitle)) {
      return directTitle
    }

    const parents = SubtaskUtil.getParentTaskIds(task.id)
    if (!parents.length) {
      return directTitle
    }

    const firstParentId = parents[0]
    const firstParent = Task.getById(firstParentId)
    if (!firstParent?.data?.subtaskList?.subtasks) {
      return directTitle
    }

    const subtaskData = firstParent.data.subtaskList.subtasks[task.id]
    return subtaskData?.title || task.title
  }

  static findContainingColumnId(taskId: DataObjectId, recur = 0): DataObjectId {
    if (recur > 3) {
      return noBoardContainerId
    }
    const box = Sync.getCurrentSandbox()
    const task = box.getHub(taskId)
    if (task && task.location !== noBoardContainerId) {
      return task.location
    }
    const parents = SubtaskUtil.getParentTaskIds(taskId)
    for (const parent of parents) {
      const colId = this.findContainingColumnId(parent, recur + 1)
      if (colId !== noBoardContainerId) {
        return colId
      }
    }
    return noBoardContainerId
  }

  static onDrop(props: {
    taskId: DataObjectId
    containerId?: DataObjectId
    taskDropAllowed?: boolean
    before?: boolean
    type?: 'drag'
  }): void {
    const task = Task.getById(props.taskId)
    if (!task || task.deleted) {
      return
    }
    const box = Sync.getCurrentSandbox()
    const data = DragData.get()
    switch (data?.type) {
      case 'user': {
        const sticker = systemStickerCtl.User.getSticker(box)
        if (!PermissionsChecker.checkAssignUser(task, data.userId)) {
          MessageBox.warn(Lang.msg.permissionsForbiddenAssignUser)
          return
        }
        Sticker.pin(sticker, props.containerId ?? 'chat', task, data.userId)
        void Tracking.send(TrackingEvent.stickerDropOnTask)
        return
      }
      case 'sticker': {
        const sticker = box.getTag(data.stickerId)
        if (
          !Task.isTaskTemplate(task) &&
          !PermissionsChecker.checkEditTaskStickers(task, sticker, undefined)
        ) {
          MessageBox.warn(Lang.msg.permissionsForbiddenTaskStickers)
          return
        }
        Sticker.pin(
          sticker,
          props.containerId ?? 'chat',
          task,
          data.stateId || data.value
        )
        void Tracking.send(TrackingEvent.stickerDropOnTask)
        return
      }
      case 'checklist': {
        if (task.id === data.taskId) {
          break
        }
        const list = getChecklists(task)
        const copy = Util.clone(data.list)
        copy.id = Util.UUID() as DataObjectId
        list.push(copy)
        Task.setChecklists(task, list)
        void Tracking.send(TrackingEvent.checklistDropOnTask)
        return
      }
      case 'task': {
        if (!props.taskDropAllowed) {
          return
        }

        const container = props.containerId && box.getHub(props.containerId)
        if (container?.data?.report) {
          return
        }

        const columnId = task.location
        const column = globals.getHubLike(columnId)
        const order = Column.getTasksOrder(column).order
        const offset = props.before ? 0 : 1
        const index = Column.listTasks(column)
          .filter(t => !Task.isArchived(t))
          .sort((a, b) => order(a) - order(b))
          .findIndex(x => x.id === task.id)
        Column.taskDropHandler(
          index + offset,
          Task.getById(data.id),
          Sync.getCurrentSandbox().getHub(columnId),
          {type: props.type}
        )
        return
      }
    }
  }

  static getByIdOrNull<T>(id: DataObjectId, cb: (task: Hub) => T): T | null {
    const task = Task.getById(id)
    if (!task || task.deleted) {
      return null
    }
    return cb(task)
  }

  static getBoardFromTask(task: Hub, deep = 0): Hub | undefined {
    const maxDepthLimit = 20
    if (!task || deep > maxDepthLimit) {
      return undefined
    }
    const box = Sync.getCurrentSandbox()
    if (box.getType(task) !== 'task') {
      return undefined
    }
    const parents = SubtaskUtil.getParentTaskIds(task.id)
    if (parents.length > 0) {
      const task = box.getHub(parents[0])
      if (task) {
        return Task.getBoardFromTask(task, deep + 1)
      }
      return undefined
    }
    const column = box.getHub(task.location)
    if (column && box.getType(column) !== 'column') {
      return undefined
    }
    const board = column && box.getHub(column.location)
    if (board && box.getType(board) !== 'board') {
      return undefined
    }
    return board
  }

  static isPhantom(hub: Hub): boolean {
    return !!hub?.properties?.phantom
  }

  static commitWithCheckPhantom(hub: Hub): void {
    if (!hub) {
      return
    }
    if (!Task.isPhantom(hub)) {
      hub.commit()
    } else {
      taskPhantomObserver.update()
    }
  }

  static isRepeatTemplate(hub?: Hub): boolean {
    return hub?.location === repeatTemplatesContainerId
  }
  static isMovementTemplate(hub?: Hub): boolean {
    return hub?.location === movementTemplatesContainerId
  }
  static isTaskTemplate(hub?: Hub): boolean {
    return hub?.location === taskTemplatesContainerId
  }

  static isTask(hub: Hub): boolean {
    if (!hub) {
      return false
    }

    const box = Sync.get(hub)
    return box.getType(hub) === 'task'
  }

  public static isDone(hub: Hub): boolean | undefined {
    if (!hub) {
      return undefined
    }

    return hub?.data?.gtd?.state?.stage === 'done'
  }

  public static getTaskColor(hub: Hub): TaskColor | undefined {
    return hub.data.color
  }

  public static isTaskColored(hub: Hub): boolean {
    const taskColor = this.getTaskColor(hub)
    return !!taskColor && taskColor !== 'task-primary'
  }

  /**
   * Checks if a task located in a column w/ auto task cover management and
   * doesn't have a manual or a cleared cover
   */
  public static mayHaveAutoTaskCover(hub: Hub): boolean {
    if (hub.data.image || hub.data.image === '') {
      return false
    }
    if (hub.location === noBoardContainerId) {
      return false
    }
    const box = Sync.getCurrentSandbox()
    const column = box.getHub(hub.location)
    if (!column) {
      return false
    }
    return Column.isAutoTaskCoverManagement(column)
  }

  /**
   * Searches last message w/ images and if exists returns it w/ its first image.
   */
  public static findLastFirstMessageWithImage(
    hub: Hub
  ): [string, ChatMessage] | undefined {
    const messages = hub.chat?.getMessages() ?? []
    for (let i = messages.length - 1; i >= 0; --i) {
      const message = messages[i]
      const firstImage = message.getLinksForAttachments().find(url => {
        const extension = FilenameUtil.originalFileFromUrl(url)
          .split('.')
          ?.pop()
          ?.split('?')[0]
        return extension && ContentTypeUtil.isImageExtension(extension)
      })

      if (firstImage) {
        return [firstImage, message]
      }
    }

    return undefined
  }

  /**
   * NOTE: THIS IS A SLOW FUNCTION! `findLastFirstMessageWithImage` is slow!
   *
   * If you need to display an auto image, check `shouldHaveAutoTaskCoverByLocation`
   * and then process `findLastFirstMessageWithImage` result, like in `TaskImage.tsx`.
   */
  static getTaskCoverType(hub: Hub): 'none' | 'cleared' | 'manual' | 'auto' {
    if (hub.data.image === '') {
      return 'cleared' // If a task is explicitly set to not have auto covers
    } else if (hub.data.image) {
      return 'manual'
    } else if (
      Task.mayHaveAutoTaskCover(hub) &&
      !!Task.findLastFirstMessageWithImage(hub)
    ) {
      return 'auto'
    } else {
      return 'none'
    }
  }

  static removeTaskCover(hub: Hub): void {
    // If a task has an auto cover, set a flag disallowing auto covers
    // '' is falsy, so old clients shouldn't break when checking if a cover exists
    hub.data.image = Task.getTaskCoverType(hub) === 'auto' ? '' : undefined
    hub.commit()
  }

  public static getAllColumnLevelParentTaskIds(
    box: SandboxTree,
    taskId: DataObjectId
  ): DataObjectId[] {
    const taskIds: DataObjectId[] = []

    const observedIds = new Set<DataObjectId>()
    let currentIds = [taskId]
    while (currentIds.length) {
      let newIds: DataObjectId[] = []
      for (const currentId of currentIds) {
        if (observedIds.has(currentId)) {
          continue
        }
        observedIds.add(currentId)

        const hub = box.getHub(currentId)
        if (!hub) {
          continue
        }
        if (hub.location && hub.location !== noBoardContainerId) {
          taskIds.push(currentId)
        }

        const parentIds = SubtaskUtil.getParentTaskIds(currentId)
        if (parentIds.length) {
          newIds = newIds.concat(parentIds)
        }
      }
      currentIds = newIds
    }

    return taskIds
  }

  public static shouldAutoPlan(hubLike?: HubLike): boolean {
    if (!hubLike) {
      return false
    }

    const box = Sync.get(hubLike)
    const hub = box.getHub(hubLike.id)

    if (!hub) {
      return false
    }

    const {useNewGantt} = CompanyPreferencesClient.get(hub.companyId)
    if (!useNewGantt) {
      return false
    }

    const checkForBoardTask = (taskId: DataObjectId): boolean => {
      const box = Sync.getCurrentSandbox()
      const task = box.getHub(taskId)

      if (
        !task ||
        box.getType(task) !== 'task' ||
        task.location === noBoardContainerId
      ) {
        return false
      }

      const column = box.getHub(task.location)
      const board = box.getHub(column?.location)
      return board?.data?.ganttSettings?.autoPlanning ?? true
    }

    return (
      checkForBoardTask(hub.id) ||
      SubtaskUtil.getAllParentTaskIds(hub.id).some(checkForBoardTask)
    )
  }
}

export const getProjectByTask = (
  taskId: DataObjectId
): {project: Hub; board: Hub} | undefined => {
  const box = Sync.getCurrentSandbox()
  const task = box.getHub(taskId)

  let boardId: DataObjectId | undefined
  if (task && task.location === noBoardContainerId) {
    const parent = SubtaskUtil.getTopParentTask(task)
    const col = parent && box.getHub(parent.location)
    boardId = col?.location
  } else {
    const col = task && box.getHub(task.location)
    boardId = col?.location
  }

  const board = boardId && box.getHub(boardId)
  const project = board && box.getHub(board.location)
  if (project && board) {
    return {project, board}
  }

  return undefined
}

export const getBoardByTask = (taskId: DataObjectId): Hub | undefined => {
  const box = Sync.getCurrentSandbox()
  const task = box.getHub(taskId)

  if (Task.isTaskTemplate(task)) {
    const boardId = task?.data?.taskTemplate?.boardId
    return boardId ? box.getHub(boardId) : undefined
  }

  const col = task && box.getHub(task.location)
  const board = col && box.getHub(col.location)
  return board
}
